import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchIncentiveProductIdsMutation } from '../services/salesIncentivesApi';
import {
  incentiveProductSetLoaded, incentiveProductSetUnchanged, incentivesHidden, selectIncentivesAccess,
} from '../store/salesIncentivesSlice';
import { isIncentiveModuleError, shouldRecheckProductSet } from '../utils/incentives';

// Owns the cached "incentive item" product set: loads it once when billing opens, rechecks (with
// If-None-Match, so usually a 304) at most every 5 minutes on a new bill or when the tab comes back.
// Errors are silent: the last good set stays. A module 403 hides every incentive UI.
export default function useIncentiveProductSet() {
  const dispatch = useDispatch();
  const access = useSelector(selectIncentivesAccess);
  const { productIds, etag, checkedAt } = useSelector((s) => s.salesIncentives);
  const [fetchSet] = useFetchIncentiveProductIdsMutation();
  const inFlight = useRef(false);
  const state = useRef({ etag, checkedAt });
  state.current = { etag, checkedAt };

  const recheck = useCallback(async (force = false) => {
    if (!access || inFlight.current) return;
    if (!force && !shouldRecheckProductSet(state.current.checkedAt)) return;
    inFlight.current = true;
    try {
      const res = await fetchSet(state.current.etag).unwrap();
      const now = Date.now();
      if (res.status === 304) dispatch(incentiveProductSetUnchanged({ checkedAt: now }));
      else dispatch(incentiveProductSetLoaded({ productIds: res.productIds, etag: res.etag, checkedAt: now }));
    } catch (err) {
      if (isIncentiveModuleError(err)) dispatch(incentivesHidden());
    } finally {
      inFlight.current = false;
    }
  }, [access, fetchSet, dispatch]);

  useEffect(() => { if (access) recheck(); }, [access, recheck]);

  useEffect(() => {
    if (!access) return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') recheck(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [access, recheck]);

  const productSet = useMemo(() => new Set(access ? productIds : []), [access, productIds]);
  const isIncentiveProduct = useCallback((productId) => productSet.has(productId), [productSet]);

  return { access, productSet, isIncentiveProduct, recheck };
}
